import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Edit } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Folder, ModelUtils } from '../../models';
import { clsx } from 'clsx';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { defineMessages, useIntl } from 'react-intl';
import { useRenameTemplatingFolder } from '../../hooks/useRenameTemplatingFolder';
import { useCanManageTemplates } from '../../hooks';

const messages = defineMessages({
  renameFolder: {
    id: 'RenameTemplatingFolderButton.renameFolder',
    defaultMessage: 'Rename folder',
  },
  modalTitle: {
    id: 'RenameTemplatingFolderModal.title',
    defaultMessage: 'Rename folder',
  },
  folderNameLabel: {
    id: 'RenameTemplatingFolderModal.folderNameLabel',
    defaultMessage: 'Folder name',
  },
  folderNameRequired: {
    id: 'RenameTemplatingFolderModal.folderNameRequired',
    defaultMessage: 'Folder name is required',
  },
  folderNameMaxLength: {
    id: 'RenameTemplatingFolderModal.folderNameMaxLength',
    defaultMessage: 'Folder name cannot exceed 256 characters',
  },
  folderNameInvalidCharacters: {
    id: 'RenameTemplatingFolderModal.folderNameInvalidCharacters',
    defaultMessage:
      'Folder name contains invalid characters. Only letters, numbers, spaces, or special characters ! _ . * ( ) - are allowed.',
  },
  cancelButton: {
    id: 'RenameTemplatingFolderModal.cancel',
    defaultMessage: 'Cancel',
  },
  saveButton: {
    id: 'RenameTemplatingFolderModal.save',
    defaultMessage: 'Save',
  },
});

const useStyles = makeStyles(theme => ({
  renameTemplatingFolderButton: {
    padding: 0,
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

type RenameTemplatingFolderButtonProps = {
  folder: Folder;
  className?: string;
};

export const RenameTemplatingFolderButton: React.FC<
  RenameTemplatingFolderButtonProps
> = ({ folder, className }) => {
  const styles = useStyles();
  const { formatMessage } = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState(ModelUtils.getName(folder));
  const { renameTemplatingFolder, isRenaming } = useRenameTemplatingFolder();
  const canManageTemplates = useCanManageTemplates();

  useEffect(() => {
    if (isModalOpen) {
      setName(ModelUtils.getName(folder));
    }
  }, [folder, isModalOpen]);

  const errorMessage = useMemo(() => {
    if (name.length === 0) {
      return formatMessage(messages.folderNameRequired);
    }
    if (name.length > 256) {
      return formatMessage(messages.folderNameMaxLength);
    }
    if (!/^[a-zA-Z0-9!_.*'() -]+$/.test(name)) {
      return formatMessage(messages.folderNameInvalidCharacters);
    }
    return undefined;
  }, [formatMessage, name]);

  const hasChanges = name !== ModelUtils.getName(folder);
  const hasError = !!errorMessage;

  const openModal: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();
      setIsModalOpen(true);
    },
    [],
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleRename = useCallback(async () => {
    const isSuccess = await renameTemplatingFolder(folder, name);
    if (isSuccess) {
      setIsModalOpen(false);
    }
  }, [folder, name, renameTemplatingFolder]);

  if (!canManageTemplates) {
    return null;
  }

  return (
    <>
      <IconButton
        size={0}
        icon={Edit}
        aria-label={formatMessage(messages.renameFolder)}
        onClick={openModal}
        className={clsx(className, styles.renameTemplatingFolderButton)}
        color="inverse"
      />
      <Modal
        isOpen={isModalOpen}
        onDismiss={closeModal}
        size={1}
        title={formatMessage(messages.modalTitle)}
        actions={
          <>
            <Button variant="text" color="negative" onClick={closeModal}>
              {formatMessage(messages.cancelButton)}
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleRename()}
              disabled={hasError || !hasChanges || isRenaming}
            >
              {formatMessage(messages.saveButton)}
            </Button>
          </>
        }
      >
        <div className={styles.modalContent}>
          <TextField
            label={formatMessage(messages.folderNameLabel)}
            aria-label={formatMessage(messages.folderNameLabel)}
            value={name}
            onChange={event => setName(event.target.value)}
            errorMessage={errorMessage}
            hasError={hasError}
            fullWidth
          />
        </div>
      </Modal>
    </>
  );
};
